import { phaseLightingSelected, phaseLightingCatalog, changePhaseLighting } from '../Utils/light_phase_utils';
import LightingSection from './Lights/LightingSection';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useSnapshot } from 'valtio';
import _ from 'lodash';
import { Box, Button, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { InfoButton } from '../FormWidgets/Description';
import useLightStyles from '../components/lightChannelStyles';
import LightCopyDialog from '../components/LightCopyDialog';
import { buildLightCopy } from '../Utils/light_copy';
import { withLightCopyZoneSettings } from '../Utils/light_copy_zone_settings';
import { persistSettingsDraft } from '../Utils/settings_draft';
import {
  channelKey,
  channelLabel,
  lightCatalogErrors,
  lightCycle,
  lightLayoutKnown,
  lightStorageAvailable,
  lightStatusUnknown
} from '../Utils/light_profiles';
import {
  discardLightDraft,
  isPendingLightCatalog,
  lightCatalogNeedsRecovery,
  lightCatalogPayload,
  submitLightChannels
} from '../Utils/light_channels_transaction';
const AdvancedLightChannels = forwardRef(function AdvancedLightChannels(
  {
    controller,
    parent,
    stateManager,
    getZoneLights,
    onZoneLightsCopied,
    onZoneLightsRestored,
    prepareCatalogForSave,
    simpleSettings,
    schedule
  },
  ref
) {
  const { t } = useTranslation();
  const classes = useLightStyles();
  const live = useSnapshot(controller);
  const active = schedule?.recipe.context;
  const scheduledSetting = (field, identity) =>
    active?.metadata.schedule_lights_enabled &&
    phaseLightingSelected(active.phase.lighting, field, identity)
      ? active
      : null;
  const baseline = live.settings.light_channels;
  const [catalog, setCatalog] = useState(() => _.cloneDeep(
    isPendingLightCatalog(controller) ? controller.temp_settings.light_channels : baseline
  ));
  const previousCatalog = useRef(baseline);
  const [error, setError] = useState('');
  const [draftActive, setDraftActive] = useState(() => isPendingLightCatalog(controller));
  // Matching stored catalogs need no Restore choice. Keep that distinction
  // through revision-only readback until a real change creates an active draft.
  const awaitingRestore = useRef(lightCatalogNeedsRecovery(controller));
  const [copyOpen, setCopyOpen] = useState(false);
  const [activeSubzone, setActiveSubzone] = useState('0');
  const zone = Number(parent.zone_id);
  const cycle = lightCycle(parent.settings.timing, active?.metadata.schedule_day_night_enabled ? active.phase : null);
  const storageUnavailable = live.settings.light_channels_info?.storage_state === 2;
  const catalogKnown = lightCatalogErrors(baseline, {}, baseline).length === 0;
  const coherentReadback = live.settings.light_channels_info?.revision === baseline.revision;
  const writeAccess = Boolean(live.access_rights?.lights?.write);
  const previousWriteAccess = useRef(writeAccess);
  const writable =
    writeAccess &&
    lightStorageAvailable(live.settings.light_channels_info) &&
    lightLayoutKnown(live.settings.light_channels_info) &&
    catalogKnown &&
    coherentReadback;
  const submission = live.light_submission;
  const retainedRevision = live.temp_settings.light_channels?.revision;
  useEffect(() => {
    if (!submission?.status || ['confirmed', 'pending', 'review'].includes(submission?.status)) setError('');
  }, [submission?.status]);
  useEffect(() => {
    const previous = previousCatalog.current;
    previousCatalog.current = baseline;
    const regainedWrite = writeAccess && !previousWriteAccess.current;
    previousWriteAccess.current = writeAccess;
    const ownReadback = submission?.status === 'confirmed' && submission.expected_revision === baseline.revision;
    const changed = previous && catalogKnown && lightCatalogErrors(previous, {}, previous).length === 0 && !_.isEqual(
      _.omit(lightCatalogPayload(previous), 'revision'), _.omit(lightCatalogPayload(baseline), 'revision'));
    if (!writeAccess) setCatalog(_.cloneDeep(baseline));
    else if (regainedWrite && draftActive && controller.temp_settings.light_channels)
      setCatalog(_.cloneDeep(controller.temp_settings.light_channels));
    else if (changed && !ownReadback && (!controller.temp_settings.light_channels ||
      (!draftActive && !awaitingRestore.current))) {
      // Without a committed draft the last readback is the committed catalog;
      // the displayed catalog may contain an unfinished slider drag.
      controller.temp_settings.light_channels = _.cloneDeep(previous);
      persistSettingsDraft(controller);
      setDraftActive(true);
    } else if (!draftActive || !controller.temp_settings.light_channels) setCatalog(_.cloneDeep(baseline));
    else if (draftActive && ['confirmed', 'review'].includes(submission?.status))
      setCatalog(_.cloneDeep(controller.temp_settings.light_channels));
  }, [baseline, catalogKnown, controller, draftActive, submission?.status,
    submission?.expected_revision, writeAccess, retainedRevision]);
  const cycles = Object.fromEntries(
    Object.values(live.zones ?? {})
      .filter((z) => z.settings?.timing)
      .map((z) => [Number(z.zone_id ?? z.id), lightCycle(z.settings.timing)])
  );
  const channels = catalog.channels.filter((c) => c.zone === zone);
  const sharedChannel = channels.find((c) => c.subzone === 0 && c.channel === 0);
  const pointCount = catalog.channels.reduce((n, c) => n + c.points.length, 0);
  const errors = lightCatalogErrors(catalog, cycles, baseline);
  const stale = baseline.revision !== catalog.revision;
  const edit = (next, saveTemp = true) => {
    if (!writable) return;
    delete controller.light_save_error;
    setCatalog(next);
    if (saveTemp) {
      awaitingRestore.current = false;
      setDraftActive(true);
      controller.temp_settings.light_channels = _.cloneDeep(next);
      localStorage.setItem(
        `${controller.id}_temp_settings`,
        JSON.stringify(controller.temp_settings)
      );
    }
    setError('');
  };
  const createShared = () => {
    if (sharedChannel) return;
    const next = {
      ...catalog,
      zones: catalog.zones.map((entry) =>
        entry.zone === zone ? { ...entry, shared_toplight: 1 } : entry
      ),
      channels: [
        ...catalog.channels,
        { zone, subzone: 0, channel: 0, name: '', minimum: 10, maximum: 100, points: [] }
      ],
      subzones: catalog.subzones.map((entry) =>
        entry.zone === zone && entry.subzone === 0
          ? { ...entry, enabled_mask: entry.enabled_mask | 1, visible_mask: entry.visible_mask | 1 }
          : entry
      )
    };
    const problems = lightCatalogErrors(next, cycles, baseline);
    if (problems.length) {
      setError(problems.join(' '));
      return;
    }
    edit(next);
  };
  const reset = () => {
    discardLightDraft(controller);
    awaitingRestore.current = false;
    setDraftActive(false);
    setCatalog(_.cloneDeep(baseline));
    setError('');
  };
  const validateCopy = (request) => {
    if (request.kind !== 'zone') return [];
    const source =
      getZoneLights?.(catalog, request.source.zone) ??
      controller.zones?.[request.source.zone]?.settings?.lights;
    const target = controller.zones?.[request.target.zone]?.settings?.lights;
    return source && target ? [] : [t('lighting_copy_missing_settings')];
  };
  const copy = (request) => {
    if (!writable || stale) return false;
    const result = buildLightCopy(catalog, request, cycles, baseline);
    const problems = [...result.errors, ...validateCopy(request)];
    if (problems.length) return false;
    let next = result.catalog;
    if (request.kind === 'zone') {
      const ordinary =
        getZoneLights?.(catalog, request.source.zone) ??
        controller.zones[request.source.zone].settings.lights;
      next = withLightCopyZoneSettings(
        next,
        request.target.zone,
        ordinary,
        controller.zones[request.target.zone].temp_settings?.lights ?? {}
      );
    }
    edit(next);
    if (request.kind === 'zone') onZoneLightsCopied?.(next, request.target.zone);
    return true;
  };
  const canSave = () => {
    // Pending is already displayed by the submission state. A second Save must
    // not manufacture a connection failure or send ordinary settings separately.
    if (controller.light_submission?.status === 'pending') return false;
    if (!catalogKnown || !writable || errors.length) return false;
    if (controller.connection?.status !== 'good') {
      controller.light_save_error = 'offline';
      return false;
    }
    if (stale && draftActive && controller.temp_settings.light_channels) {
      return false;
    }
    return true;
  };
  useImperativeHandle(ref, () => ({
    reset,
    getCatalog: () => prepareCatalogForSave?.(catalog) ?? catalog,
    canSave,
    hasChanges: () => Boolean(draftActive && controller.temp_settings.light_channels),
    restoreTempSettings: () => {
      awaitingRestore.current = false;
      const restored = _.cloneDeep(controller.temp_settings.light_channels || baseline);
      // Old browser drafts cannot supply the newly required zone controls. Drop
      // incompatible drafts rather than inventing settings or partially saving them.
      if (
        !Array.isArray(restored.zones) ||
        restored.zones.length !== 9 ||
        !Array.from({ length: 9 }, (_, id) => id).every((id) =>
          restored.zones.some((entry) => entry?.zone === id)
        ) ||
        !Array.isArray(restored.subzones) ||
        restored.subzones.length !== 36 ||
        restored.subzones.some((entry) => ![0, 1].includes(entry?.enabled)) ||
        !Array.isArray(restored.channels)
      ) {
        reset();
        return;
      }
      // Explicit Restore/Paste is the user's choice to apply the retained settings
      // to the currently read catalog. Active concurrent edits are never rebased.
      restored.revision = baseline.revision;
      setDraftActive(true);
      setCatalog(restored);
      controller.temp_settings.light_channels = restored;
      onZoneLightsRestored?.(restored);
      localStorage.setItem(
        `${controller.id}_temp_settings`,
        JSON.stringify(controller.temp_settings)
      );
    },
    save: () => {
      if (!canSave()) return false;
      if (!draftActive || !controller.temp_settings.light_channels) return true;
      if (
        errors.length ||
        stale ||
        !submitLightChannels(
          controller,
          prepareCatalogForSave?.(catalog) ?? catalog,
          stateManager.wsManager
        )
      ) {
        controller.light_save_error = stale ? 'conflict' : 'unsent';
        return false;
      }
      return true;
    }
  }));
  const runtime = live.settings.light_channels_info;
  const runtimeAvailable = runtime?.schema_version === 1;
  const fallback = Boolean(runtime?.fallback_zone_mask & (1 << zone));
  const faultFlags = runtime?.zone_faults?.[zone] ?? 0;
  const faultText = [
    [1, 'lighting_fault_profile', 'invalid profile'],
    [2, 'lighting_fault_storage', 'profile storage unavailable'],
    [4, 'lighting_fault_time', 'invalid Time settings'],
    [8, 'lighting_fault_core', 'installation settings unavailable'],
    [~15, 'unknown', 'Unknown']
  ]
    .filter(([flag]) => faultFlags & flag)
    .map(([, key, defaultValue]) => t(key, { defaultValue }))
    .join(', ');
  // Preserve unfamiliar catalog values without interpreting them as switches or
  // clamping them through editable controls. Reported output remains visible.
  if (!catalogKnown)
    return (
      <Box className={classes.root}>
        <Alert severity="warning">{t('lighting_unknown_settings')}</Alert>
        {(runtime?.channels ?? []).map((status) => (
          <Typography key={channelKey(status)}>
            {channelLabel(
              (baseline.channels ?? []).find(
                (channel) => channelKey(channel) === channelKey(status)
              ) ?? status
            )}
            {' | '}
            {t('lighting_requested')} {status.requested / 10}
            {t('lighting_actual')} {status.actual / 10}%
          </Typography>
        ))}
      </Box>
    );
  const projected = active?.metadata.schedule_lights_enabled
    ? phaseLightingCatalog(catalog, zone, active.phase.lighting) : catalog;
  const savedPhase = schedule?.recipe.baseline?.schedule_values?.scheduling_values.find(
    (phase) => phase.phase_id === active?.phaseId);
  const savedProjected = active?.metadata.schedule_lights_enabled
    ? phaseLightingCatalog(baseline, zone, savedPhase?.lighting) : baseline;
  const advanced = Boolean(projected.advanced_zone_mask & (1 << zone));
  const scheduleWritable = Boolean(schedule?.writable && catalogKnown && coherentReadback &&
    lightStorageAvailable(runtime) && lightLayoutKnown(runtime));
  const scheduledControls = {
    writable: scheduleWritable,
    canReset: (field, identity) => Boolean(savedPhase && phaseLightingSelected(savedPhase.lighting, field, identity)),
    profilePointCount: (active?.phase.lighting?.channels ?? []).reduce((count, channel) => count + channel.points.length, 0),
    change(field, identity, value, saveTemp = true) {
      if (!scheduledSetting(field, identity) || !scheduleWritable) return;
      schedule.recipe.controls.setValue(
        `schedule_values.scheduling_values[${schedule.recipe.phaseIndex}].lighting`,
        changePhaseLighting(active.phase.lighting, field, identity, value,
          field !== 'channel_profile' || value.length > 0), saveTemp);
    }
  };
  return (
    <LightingSection
      controller={controller}
      scheduledSetting={scheduledSetting}
      scheduledControls={scheduledControls}
      catalog={projected}
      ordinaryCatalog={catalog}
      baseline={savedProjected}
      ordinaryBaseline={baseline}
      zone={zone}
      cycle={cycle}
      writable={writable}
      runtime={runtime}
      sunriseDuration={(simpleSettings ?? parent.settings.lights)?.light_sunrise_duration ?? 0}
      subzoneCount={Number(parent.settings.lights?.lights_sub_zones_active_cnt ?? 4)}
      onChange={edit}
      onCreateShared={createShared}
      onActiveSubzoneChange={setActiveSubzone}
      feedback={
        <>
          {runtimeAvailable && fallback && (
            <Alert severity="warning">{t('lighting_fallback_reason', { reason: faultText })}</Alert>
          )}
          {runtimeAvailable && lightStatusUnknown(runtime) && (
            <Alert severity="warning">{t('lighting_unknown_status')}</Alert>
          )}
          {runtimeAvailable && runtime.storage_state === 1 && (
            <Alert severity="warning">{t('lighting_storage_cached_warning')}</Alert>
          )}
          {!coherentReadback && <Alert severity="info">{t('lighting_wait_readback')}</Alert>}
          {storageUnavailable && (
            <Alert severity="warning">
              {t(
                runtime?.zone_faults?.some((fault) => fault & 8)
                  ? 'lighting_core_unavailable'
                  : 'lighting_storage_unavailable',
                {
                  defaultValue:
                    'Profile storage is unavailable. Simple fallback continues; editing and saving are disabled until a complete lighting configuration can be read. Your draft is retained.'
                }
              )}
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {errors.map((message, i) => (
            <Alert severity="error" key={i}>
              {message}
            </Alert>
          ))}
        </>
      }
      footer={
        <>
          {advanced && (
            <Box className={classes.actions}>
              <Button
                color="primary"
                variant="outlined"
                disabled={!writable || stale}
                onClick={() => setCopyOpen(true)}
              >
                {t('lighting_copy_title')}
              </Button>
            </Box>
          )}
          {advanced && copyOpen && (
            <LightCopyDialog
              controller={controller}
              currentZone={zone}
              currentSubzone={Number(activeSubzone)}
              catalog={catalog}
              cycles={cycles}
              baseline={baseline}
              disabled={!writable || stale}
              validateCopy={validateCopy}
              onClose={() => setCopyOpen(false)}
              onApply={copy}
            />
          )}
          {advanced && (
            <Box className={classes.actions}>
              <Typography variant="caption" color="textSecondary">
                {t('lighting_capacity_summary', {
                  channels: catalog.channels.length,
                  points: pointCount
                })}
              </Typography>
              <InfoButton
                brief={t('lighting_capacity_help')}
                content={t('lighting_capacity_details', {
                  count: channels.length,
                  revision: baseline.revision
                })}
              />
            </Box>
          )}
        </>
      }
    />
  );
});
export default AdvancedLightChannels;
